import React, { useLayoutEffect, useRef } from 'react';
import { consumeSkipPageEnter } from 'pages/components/Loader/bootReveal';
import * as S from './PageEnter.styled';

type ScreenEnterProps = {
  enterKey: string;
  children: React.ReactNode;
  /** Use for in-page panels so layout stays with the parent page shell. */
  inline?: boolean;
};

/** Survives React 18 Strict Mode remount so enter anim does not double-flash. */
const recentEnterAt = new Map<string, number>();
const ENTER_MS = 520;
const STRICT_REMOUNT_MS = 120;

/**
 * Shared entrance motion for full pages and in-page subviews.
 * Pass a stable `enterKey` that changes when the animated region should replay.
 * Use `inline` inside a page shell (CRM tabs, Settings subpages) so only that region moves.
 */
const ScreenEnter = function ScreenEnter({
  enterKey,
  children,
  inline = false,
}: ScreenEnterProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const Surface = inline ? S.InlineRoot : S.Root;

  useLayoutEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;

    // After HTML boot splash, skip opacity-0 enter so the first reveal is not a white frame.
    if (consumeSkipPageEnter()) {
      node.classList.remove('is-entering');
      recentEnterAt.set(enterKey, Date.now());
      return undefined;
    }

    const now = Date.now();
    const prev = recentEnterAt.get(enterKey) || 0;
    // Strict Mode remounts effects immediately — do not restart the enter flash.
    if (now - prev < STRICT_REMOUNT_MS) {
      const remaining = Math.max(0, ENTER_MS - (now - prev));
      const clear = window.setTimeout(() => {
        node.classList.remove('is-entering');
      }, remaining);
      return () => window.clearTimeout(clear);
    }

    recentEnterAt.set(enterKey, now);
    node.classList.remove('is-entering');
    // Force a reflow so the next class add restarts CSS animations.
    node.getBoundingClientRect();
    node.classList.add('is-entering');

    const clear = window.setTimeout(() => {
      node.classList.remove('is-entering');
    }, ENTER_MS);

    return () => {
      window.clearTimeout(clear);
      // Do not strip is-entering here — Strict Mode cleanup+rerun was flashing
      // opacity 0 twice on every navigation.
    };
  }, [enterKey, inline]);

  return (
    <Surface ref={rootRef} data-page-enter={enterKey}>
      {children}
    </Surface>
  );
};

export default ScreenEnter;
